import { Box, Chip, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type {
  EmployerVerificationListItem,
  PersistedGateCheckpointDto,
} from '../../api/employerVerificationDashboardApi';
import { GATE_ORDER_PIPE, gateMeta, type GateKey } from '../../lib/employerVerificationCopy';
import {
  rollupGateDisplayBucket,
  resolvedCheckpointDisplayStatus,
  type GateDisplayBucket,
} from '../../lib/employerVerificationGateAudit';
import { formatGateReasonReadableFlow } from '../../lib/employerVerificationGateReasonFormat';

function statusChipFromBucket(bucket: GateDisplayBucket): {
  label: string;
  color: 'success' | 'error' | 'warning' | 'default';
} {
  if (bucket === 'fail') return { label: 'Fail', color: 'error' };
  if (bucket === 'review') return { label: 'Review', color: 'warning' };
  if (bucket === 'pass') return { label: 'Pass', color: 'success' };
  return { label: 'N/A', color: 'default' };
}

function synopsisFromAudit(
  gate: GateKey,
  audit: Record<string, PersistedGateCheckpointDto[]> | null | undefined,
): string | null {
  const steps = audit?.[gate];
  if (!steps?.length) return null;

  const fail = steps.find(
    (c) => resolvedCheckpointDisplayStatus(c) === 'FAIL',
  );
  if (fail) return fail.reason?.trim() || `Failed at ${fail.step}`;
  const review = steps.find(
    (c) => resolvedCheckpointDisplayStatus(c) === 'REVIEW',
  );
  if (review) return review.reason?.trim() || `Needs review at ${review.step}`;
  const allPass = steps.every(
    (c) => resolvedCheckpointDisplayStatus(c) === 'PASS',
  );
  if (allPass) return 'All recorded checkpoints in this gate passed.';

  return steps
    .map((c) => `${c.step}: ${resolvedCheckpointDisplayStatus(c)}`)
    .slice(0, 3)
    .join('\n');
}

type Props = {
  gates: EmployerVerificationListItem['gates'];
  gateMessages: Record<string, string | null>;
  gateCheckpointAudit: Record<string, PersistedGateCheckpointDto[]> | null | undefined;
};

export function GateStatusOverviewSection({ gates, gateMessages, gateCheckpointAudit }: Props) {
  const theme = useTheme();

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
        Gates overview
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25, lineHeight: 1.55 }}>
        Each row is the <strong>applicant’s result for that gate</strong> (Pass / Fail / Review). Review at gate level is
        the automation’s “needs human judgment” lane — it often lines up with a final <strong>Needs review</strong>{' '}
        decision when policy cannot auto-approve. Checkpoint-level Pass / Fail / Review for every sub-step is in the
        section below.
      </Typography>
      <Stack spacing={1}>
        {GATE_ORDER_PIPE.map((gk) => {
          const raw = (gates[gk] ?? '').trim();
          const steps = gateCheckpointAudit?.[gk];
          const chip = statusChipFromBucket(rollupGateDisplayBucket(raw, steps));
          const meta = gateMeta[gk];
          const msg = typeof gateMessages[gk] === 'string' ? gateMessages[gk]?.trim() : '';
          const fromAudit = synopsisFromAudit(gk, gateCheckpointAudit);
          const rawBody = msg || fromAudit || 'No stored summary for this gate — use checkpoint detail or JSON for this run.';
          const body = formatGateReasonReadableFlow(rawBody);

          return (
            <Box
              key={gk}
              sx={{
                border: '1px solid',
                borderColor: alpha(theme.palette.divider, 0.9),
                borderRadius: 2,
                p: 1.15,
                bgcolor: 'background.paper',
              }}
            >
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1} flexWrap="wrap">
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {meta.short} · {meta.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    {meta.line}
                  </Typography>
                </Box>
                <Chip size="small" label={chip.label} color={chip.color} sx={{ fontWeight: 800, flexShrink: 0 }} />
              </Stack>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1, lineHeight: 1.55, whiteSpace: 'pre-line', wordBreak: 'break-word' }}
              >
                {body}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
